<template>
  <div class="flex flex-col w-full max-w-[80%]">
    <div
      v-if="updateProgramLoading"
      class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    >
      <div
        class="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary-purple"
      ></div>
    </div>
    <div v-if="errorMessage" class="mb-2 text-red-600 text-sm">
      Something went wrong. Please try again later.
    </div>
    <div class="flex flex-col gap-4 w-full">
      <div class="flex flex-row items-center gap-2">
        <div
          v-html="PAYOUT_HEART_ICON"
          class="text-primary-purple w-6 h-6"
        ></div>
        <div class="flex flex-col">
          <div
            class="text-black font-inter text-base font-semibold normal-case leading-6"
          >
            Meeting duration
          </div>
        </div>
      </div>
      <div
        class="flex items-center justify-between p-4 rounded-lg border border-gray-200 w-full"
      >
        <div class="flex items-center gap-3">
          <span class="text-zinc-800 text-sm font-medium">
            Thank you payout only if meeting duration is more than 5 minutes
          </span>
        </div>
        <div class="flex items-center">
          <button
            type="button"
            role="switch"
            :aria-checked="payoutOnlyCallDurationMoreThan5"
            @click="togglePayoutSetting"
            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-600 focus:ring-offset-2"
            :class="
              payoutOnlyCallDurationMoreThan5 ? 'bg-violet-600' : 'bg-gray-200'
            "
            :disabled="updateProgramLoading"
          >
            <span
              class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              :class="
                payoutOnlyCallDurationMoreThan5
                  ? 'translate-x-6'
                  : 'translate-x-1'
              "
            />
          </button>
        </div>
      </div>
      <div class="flex flex-col">
        <ExpertTags
          :initialExpertTags="expertTags"
          :initialShowExpertTagsList="showExpertTagsList"
          @updateExpertTags="updateExpertTags"
          @saveExpertTags="saveExpertTags"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue";
import { gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { PAYOUT_HEART_ICON } from "@/assets/svg/shared/svg-constants";
import ExpertTags from "../advanced-program-settings/ExpertTags.vue";
import { UpdateProgramInput } from "@/types/expert-program-types";

const UPDATE_PROGRAM = gql`
  mutation updateProgram($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      program {
        id
      }
      errors
    }
  }
`;

export default defineComponent({
  name: "AdvancedProgramSettings",
  components: {
    ExpertTags,
  },
  props: {
    programId: {
      type: String,
      required: true,
    },
    initialPayoutOnlyCallDurationMoreThan5: {
      type: Boolean,
      required: true,
    },
    initialExpertTags: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },
  emits: ["programUpdated"],
  setup(props, { emit }) {
    const payoutOnlyCallDurationMoreThan5 = ref(
      props.initialPayoutOnlyCallDurationMoreThan5
    );
    const expertTags = ref<string[]>(props.initialExpertTags);
    const showExpertTagsList = ref(props.initialExpertTags.length > 0);
    const errorMessage = ref("");

    watch(
      () => props.initialPayoutOnlyCallDurationMoreThan5,
      (newVal) => {
        payoutOnlyCallDurationMoreThan5.value = newVal;
      }
    );

    watch(
      () => props.initialExpertTags,
      (newVal) => {
        expertTags.value = newVal;
        showExpertTagsList.value = newVal.length > 0;
      }
    );

    const {
      mutate: updateProgram,
      onDone: updateProgramDone,
      onError: updateProgramError,
      loading: updateProgramLoading,
    } = useMutation(UPDATE_PROGRAM);

    const togglePayoutSetting = () => {
      updateProgramSettings("payoutOnlyCallDurationMoreThan5");
    };

    const determineProgramParams = (updateProgramSetting: string) => {
      const programParams: UpdateProgramInput = {
        id: props.programId,
      };

      if (updateProgramSetting === "payoutOnlyCallDurationMoreThan5") {
        programParams.payoutOnlyCallDurationMoreThan5 =
          !payoutOnlyCallDurationMoreThan5.value;
      }

      if (updateProgramSetting === "expertTags") {
        programParams.expertTags = expertTags.value;
      }

      return programParams;
    };

    const handleSuccess = (updatedProgramSetting: string) => {
      if (updatedProgramSetting === "payoutOnlyCallDurationMoreThan5") {
        payoutOnlyCallDurationMoreThan5.value =
          !payoutOnlyCallDurationMoreThan5.value;
        emit("programUpdated");
      }
    };

    const updateProgramSettings = (updateProgramSetting: string) => {
      errorMessage.value = "";
      updateProgram({
        input: {
          programParams: determineProgramParams(updateProgramSetting),
          claimProgram: false,
        },
      });
      updateProgramDone((response) => {
        if (response) {
          if (
            response.data.updateProgram.errors &&
            response.data.updateProgram.errors.length > 0
          ) {
            handleError(response.data.updateProgram.errors.join(". "));
          } else {
            handleSuccess(updateProgramSetting);
          }
        }
      });
      updateProgramError((error) => {
        handleError(error.message);
      });
    };

    const updateExpertTags = (newExpertTags: string[]) => {
      expertTags.value = newExpertTags;
    };

    const saveExpertTags = () => {
      updateProgramSettings("expertTags");
    };

    const handleError = (message: string) => {
      errorMessage.value = message;
      setTimeout(() => {
        errorMessage.value = "";
      }, 2500);
    };

    return {
      payoutOnlyCallDurationMoreThan5,
      togglePayoutSetting,
      errorMessage,
      updateProgramLoading,
      updateExpertTags,
      saveExpertTags,
      expertTags,
      showExpertTagsList,
      PAYOUT_HEART_ICON,
    };
  },
});
</script>
